/**
 * Shared WebAuthn helpers used by both the passkey login button and the
 * passkey settings page. Pure functions only — no DOM access, no fetch.
 *
 * Exposed as `window.WebAuthnHelpers` so flat scripts loaded after this
 * one (passkey-login.js, passkey-settings.js, etc.) can pick it up
 * without an ES-module import. Webpack bundles this file as part of the
 * main entry; the test harness loads it explicitly via vm.runInContext.
 */
(function () {
    'use strict';

    const MAX_PASSKEY_NAME_LENGTH = 64;

    function base64UrlEncode(bytes) {
        let str = '';
        const view = new Uint8Array(bytes);
        for (let i = 0; i < view.byteLength; i++) {
            str += String.fromCharCode(view[i]);
        }
        return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }

    function base64UrlDecode(b64u) {
        const padded = b64u.replace(/-/g, '+').replace(/_/g, '/');
        const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
        const bin = atob(padded + padding);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
            bytes[i] = bin.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * Convert the server's PublicKeyCredentialRequestOptions JSON into the
     * binary-buffer shape navigator.credentials.get expects.
     */
    function decodeRequestOptions(publicKey) {
        const decoded = { ...publicKey };
        if (typeof publicKey.challenge === 'string') {
            decoded.challenge = base64UrlDecode(publicKey.challenge);
        }
        if (Array.isArray(publicKey.allowCredentials)) {
            decoded.allowCredentials = publicKey.allowCredentials.map((c) => ({
                ...c,
                id: typeof c.id === 'string' ? base64UrlDecode(c.id) : c.id,
            }));
        }
        return decoded;
    }

    /**
     * Convert the server's PublicKeyCredentialCreationOptions JSON into
     * the binary-buffer shape navigator.credentials.create expects.
     */
    function decodeCreateOptions(publicKey) {
        const decoded = { ...publicKey };
        if (typeof publicKey.challenge === 'string') {
            decoded.challenge = base64UrlDecode(publicKey.challenge);
        }
        if (publicKey.user && typeof publicKey.user.id === 'string') {
            decoded.user = { ...publicKey.user, id: base64UrlDecode(publicKey.user.id) };
        }
        if (Array.isArray(publicKey.excludeCredentials)) {
            decoded.excludeCredentials = publicKey.excludeCredentials.map((c) => ({
                ...c,
                id: typeof c.id === 'string' ? base64UrlDecode(c.id) : c.id,
            }));
        }
        return decoded;
    }

    function detectBrowserLabel(userAgent) {
        const ua = userAgent || '';
        if (/Edg\//.test(ua)) return 'Edge';
        if (/OPR\//.test(ua)) return 'Opera';
        if (/SamsungBrowser\//.test(ua)) return 'Samsung Internet';
        if (/CriOS\//.test(ua) || /Chrome\//.test(ua)) return 'Chrome';
        if (/FxiOS\//.test(ua) || /Firefox\//.test(ua)) return 'Firefox';
        if (/Version\/.+Safari\//.test(ua)) return 'Safari';
        return '';
    }

    function detectPlatformLabel(userAgent, platformHint, maxTouchPoints) {
        const ua = userAgent || '';
        const platform = platformHint || '';
        const touch = typeof maxTouchPoints === 'number' ? maxTouchPoints : 0;

        if (/iPhone/i.test(ua)) return 'iPhone';
        if (/iPad/i.test(ua) || (platform === 'MacIntel' && touch > 1)) return 'iPad';
        if (/Android/i.test(ua)) return 'Android';
        if (/CrOS/i.test(ua)) return 'ChromeOS';
        if (/Mac/i.test(platform)) return 'Mac';
        if (/Win/i.test(platform)) return 'Windows';
        if (/Linux/i.test(platform)) return 'Linux';
        return '';
    }

    function getSuggestedPasskeyName(defaultName, browser, platform) {
        const base = defaultName || 'Passkey';
        let name = base;
        if (browser && platform) {
            name = `${browser} ${base} (${platform})`;
        } else if (browser) {
            name = `${browser} ${base}`;
        } else if (platform) {
            name = `${platform} ${base}`;
        }
        return name.slice(0, MAX_PASSKEY_NAME_LENGTH);
    }

    /**
     * Pulls the per-field error code from a validation response so callers
     * can branch on `password_not_set` vs `password_incorrect`. Returns
     * null for any non-validation response or when the body is unreadable.
     *
     * The API envelope wraps errors as `{ error: { code, errors: {...} } }`,
     * while ajax responses can return `{ errors: {...} }` directly. Prefer
     * the per-field map, then fall back to top-level password codes only for
     * the two unambiguous password failures.
     */
    async function readPasswordErrorCode(res) {
        try {
            const data = await res.clone().json();
            const envelope = data?.error;
            const fieldCode = envelope?.errors?.currentPassword?.code
                || data?.errors?.currentPassword?.code;
            if (typeof fieldCode === 'string' && fieldCode !== '') {
                return fieldCode;
            }
            const topCode = envelope?.code;
            if (typeof topCode === 'string'
                && (topCode === 'password_incorrect' || topCode === 'password_not_set')) {
                return topCode;
            }
            return null;
        } catch (_e) {
            return null;
        }
    }

    /**
     * Maps a non-OK response to the right user-facing message key.
     * Returns an object the caller resolves to a localized string. 429
     * → rate limited. 400 is split by where the failure lives: if the
     * server flagged the currentPassword field, surface the password
     * branch (password_not_set for OAuth-only users, password_invalid
     * otherwise). 400s without a currentPassword error (e.g. a name
     * validation failure) fall through to the generic message so the
     * user is not told their password is wrong when it isn't.
     */
    async function classifyPasskeyError(res) {
        if (res.status === 429) {
            return { kind: 'rate_limited' };
        }
        if (res.status === 400) {
            const code = await readPasswordErrorCode(res);
            if (code === 'password_not_set') {
                return { kind: 'password_not_set' };
            }
            if (code !== null) {
                return { kind: 'password_invalid' };
            }
        }
        return { kind: 'generic' };
    }

    function isWebAuthnSupported() {
        return typeof window !== 'undefined'
            && window.isSecureContext === true
            && typeof window.PublicKeyCredential === 'function';
    }

    /**
     * Look up a translated string with the project's two translator
     * shims (window.t for dotted keys, window.trans for domain+key)
     * and fall back to the supplied English string if neither resolves.
     */
    function translate(domain, key, fallback) {
        const dottedKey = `${domain}.${key}`;
        if (typeof window.t === 'function') {
            const translated = window.t(dottedKey);
            if (translated && translated !== dottedKey) {
                return translated;
            }
        }
        if (typeof window.trans === 'function') {
            const translated = window.trans(domain, key);
            if (translated) {
                return translated;
            }
        }
        return fallback;
    }

    window.WebAuthnHelpers = {
        MAX_PASSKEY_NAME_LENGTH,
        base64UrlEncode,
        base64UrlDecode,
        decodeRequestOptions,
        decodeCreateOptions,
        detectBrowserLabel,
        detectPlatformLabel,
        getSuggestedPasskeyName,
        readPasswordErrorCode,
        classifyPasskeyError,
        isWebAuthnSupported,
        translate,
    };
})();
